import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tabsContainer", "panelsContainer", "template", "tabTemplate", "positionInput"]
  static values = {
    activeIndex: { type: Number, default: 0 },
    confirmRemove: { type: String, default: "Remove this variant?" },
    label: { type: String, default: "Variants:" },
    productId: { type: String, default: "new" },
    addTitle: { type: String, default: "Add variant" },
    removeTitle: { type: String, default: "Remove variant" },
    toggleTitle: { type: String, default: "Toggle variant values" }
  }

  connect() {
    this.isConnected = true
    this.showValues = this.readShowValuesPreference()
    this.updateTabs = this.updateTabs.bind(this)
    this.createTabButton = this.createTabButton.bind(this)
    this.variantSummary = this.variantSummary.bind(this)
    this.escapeHtml = this.escapeHtml.bind(this)
    this.handleVariantLabelChange = this.handleVariantLabelChange.bind(this)
    this.scheduleTabRefresh = this.scheduleTabRefresh.bind(this)
    this.element.addEventListener('input', this.handleVariantLabelChange)
    this.element.addEventListener('change', this.handleVariantLabelChange)
    this.element.addEventListener('variant:labels-changed', this.scheduleTabRefresh)

    if (this.hasPanelsContainerTarget) {
      this.observer = new MutationObserver(this.scheduleTabRefresh)
      this.observer.observe(this.panelsContainerTarget, { childList: true, subtree: true })
    }

    this.updateTabs()
    this.showTab(this.activeIndexValue)
    this.updatePositions()
  }

  disconnect() {
    this.isConnected = false
    this.element.removeEventListener('input', this.handleVariantLabelChange)
    this.element.removeEventListener('change', this.handleVariantLabelChange)
    this.element.removeEventListener('variant:labels-changed', this.scheduleTabRefresh)

    if (this.observer) {
      this.observer.disconnect()
    }

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout)
    }
  }

  handleVariantLabelChange(event) {
    if (!event.target.closest('[data-variant-attribute-field]')) return

    this.scheduleTabRefresh()
  }

  scheduleTabRefresh() {
    if (!this.isConnected) return

    clearTimeout(this.refreshTimeout)
    this.refreshTimeout = setTimeout(() => {
      if (!this.isConnected) return

      this.updateTabs()
    }, 20)
  }

  updateTabs() {
    const panels = this.getPanels()
    const distinguishingKeys = this.showValues ? this.distinguishingVariantAttributeKeys(panels) : new Set()
    const tabsHtml = panels.map((panel, index) => this.createTabButton(index, panels.length, panel, distinguishingKeys)).join('')

    // Label before tabs
    const label = `
      <span class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300">
        ${this.escapeHtml(this.labelValue)}
        ${this.valuesToggleButton()}
      </span>
    `

    // Add the "+" button at the end
    const addButton = `
      <button type="button"
              data-action="click->variant-tabs#addVariant"
              class="px-3 py-2 text-slate-400 hover:text-teal-500 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
              title="${this.addTitleValue}">
        <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
        </svg>
      </button>
    `

    this.tabsContainerTarget.innerHTML = label + tabsHtml + addButton
  }

  createTabButton(index, totalPanels, panel, distinguishingKeys = new Set()) {
    const isActive = index === this.activeIndexValue
    const activeClasses = isActive
      ? 'border-teal-500 text-teal-600 dark:text-teal-400 bg-white dark:bg-slate-800'
      : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'

    // Only show remove button if more than 1 variant
    const removeButton = totalPanels > 1 ? `
      <button type="button"
              data-action="click->variant-tabs#confirmRemove"
              data-index="${index}"
              class="ml-1 p-0.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/50 rounded transition-colors cursor-pointer"
              title="${this.removeTitleValue}">
        <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    ` : ''

    const summary = this.showValues ? this.variantSummary(panel, distinguishingKeys) : ''
    const summaryHtml = summary
      ? `<span class="ml-1 max-w-40 truncate text-xs font-normal text-slate-500 dark:text-slate-400">${this.escapeHtml(summary)}</span>`
      : ''

    return `
      <div class="variant-tab flex items-center px-3 py-2 border-b-2 transition-colors cursor-grab ${activeClasses}"
           draggable="true"
           data-index="${index}"
           data-action="dragstart->variant-tabs#dragStart dragend->variant-tabs#dragEnd dragover->variant-tabs#dragOver drop->variant-tabs#drop">
        <button type="button"
                data-action="click->variant-tabs#selectTab"
                data-index="${index}"
                class="flex min-w-0 items-center text-sm font-medium cursor-pointer">
          ${index + 1}
          ${summaryHtml}
        </button>
        ${removeButton}
      </div>
    `
  }

  toggleValues(event) {
    event.preventDefault()
    event.stopPropagation()

    this.showValues = !this.showValues
    this.writeShowValuesPreference()
    this.updateTabs()
  }

  valuesToggleButton() {
    const activeClasses = this.showValues
      ? 'text-teal-600 bg-teal-50 hover:bg-teal-100 dark:text-teal-300 dark:bg-teal-900/30 dark:hover:bg-teal-900/50'
      : 'text-slate-400 bg-slate-100 hover:bg-slate-200 dark:text-slate-500 dark:bg-slate-800 dark:hover:bg-slate-700'

    return `
      <button type="button"
              data-action="click->variant-tabs#toggleValues"
              class="inline-flex h-6 w-6 items-center justify-center rounded border border-transparent transition-colors ${activeClasses}"
              title="${this.toggleTitleValue}">
        ${this.showValues ? this.eyeIcon() : this.eyeSlashIcon()}
      </button>
    `
  }

  readShowValuesPreference() {
    return this.getCookie(this.showValuesCookieName()) !== 'false'
  }

  writeShowValuesPreference() {
    const expires = new Date()
    expires.setFullYear(expires.getFullYear() + 1)
    document.cookie = `${this.showValuesCookieName()}=${this.showValues ? 'true' : 'false'}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
  }

  showValuesCookieName() {
    return `product_${this.productIdValue}_variant_tab_values`
  }

  getCookie(name) {
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`))

    return match ? decodeURIComponent(match[1]) : null
  }

  eyeIcon() {
    return `
      <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.7" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.433 0 .64C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z"/>
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
      </svg>
    `
  }

  eyeSlashIcon() {
    return `
      <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.7" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
      </svg>
    `
  }

  distinguishingVariantAttributeKeys(panels) {
    const valuesByKey = new Map()

    panels.forEach((panel) => {
      this.variantAttributeEntries(panel).forEach(({ key, value }) => {
        if (!valuesByKey.has(key)) {
          valuesByKey.set(key, new Set())
        }

        valuesByKey.get(key).add(value.toLowerCase())
      })
    })

    return new Set(
      Array.from(valuesByKey.entries())
        .filter(([, values]) => values.size > 1)
        .map(([key]) => key)
    )
  }

  variantSummary(panel, distinguishingKeys = new Set()) {
    if (!panel) return ''

    return this.variantAttributeEntries(panel)
      .filter(({ key, value }) => distinguishingKeys.has(key) && value)
      .map(({ value }) => value)
      .join(' / ')
  }

  variantAttributeEntries(panel) {
    if (!panel) return []

    return Array.from(panel.querySelectorAll('[data-variant-attribute-field]'))
      .map((field) => {
        const input = field.querySelector('input[name*="[value]"], select[name*="[value]"], textarea[name*="[value]"]')
        const value = input?.value?.trim() || ''
        const key = field.dataset.variantAttributeId || field.dataset.variantAttributeName
        if (!key) return null

        return { key: String(key), value }
      })
      .filter(Boolean)
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;')
  }

  // Drag and drop handlers
  dragStart(event) {
    this.draggedIndex = parseInt(event.currentTarget.dataset.index)
    event.currentTarget.classList.add('opacity-50')
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', this.draggedIndex)
  }

  dragEnd(event) {
    event.currentTarget.classList.remove('opacity-50')
    // Remove any drag-over styling
    this.tabsContainerTarget.querySelectorAll('.variant-tab').forEach(tab => {
      tab.classList.remove('border-l-2', 'border-l-teal-500')
    })
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'

    const tab = event.currentTarget
    const targetIndex = parseInt(tab.dataset.index)

    // Add visual indicator
    this.tabsContainerTarget.querySelectorAll('.variant-tab').forEach(t => {
      t.classList.remove('border-l-2', 'border-l-teal-500')
    })

    if (targetIndex !== this.draggedIndex) {
      tab.classList.add('border-l-2', 'border-l-teal-500')
    }
  }

  drop(event) {
    event.preventDefault()
    const targetIndex = parseInt(event.currentTarget.dataset.index)

    if (this.draggedIndex === targetIndex) return

    this.reorderPanels(this.draggedIndex, targetIndex)
  }

  reorderPanels(fromIndex, toIndex) {
    const panels = this.getPanels()
    const panel = panels[fromIndex]

    if (!panel) return

    // Get the target panel
    const targetPanel = panels[toIndex]

    // Move the panel in the DOM
    if (fromIndex < toIndex) {
      targetPanel.after(panel)
    } else {
      targetPanel.before(panel)
    }

    // Update positions for all panels
    this.updatePositions()

    // Update active index if needed
    if (this.activeIndexValue === fromIndex) {
      this.activeIndexValue = toIndex
    } else if (fromIndex < this.activeIndexValue && toIndex >= this.activeIndexValue) {
      this.activeIndexValue--
    } else if (fromIndex > this.activeIndexValue && toIndex <= this.activeIndexValue) {
      this.activeIndexValue++
    }

    this.updateTabs()
    this.showTab(this.activeIndexValue)
  }

  updatePositions() {
    const panels = this.getPanels()
    panels.forEach((panel, index) => {
      const positionInput = panel.querySelector('input[name*="[position]"]')
      if (positionInput) {
        positionInput.value = index + 1
      }
      // Update data attribute for reference
      panel.dataset.variantPosition = index + 1
    })
  }

  confirmRemove(event) {
    event.stopPropagation()
    if (confirm(this.confirmRemoveValue)) {
      const index = parseInt(event.currentTarget.dataset.index)
      this.removeVariantAt(index)
    }
  }

  removeVariantAt(index) {
    const panels = this.getPanels()
    const panel = panels[index]
    if (!panel) return

    // Mark for destruction
    const destroyInput = panel.querySelector('input[name*="_destroy"]')
    if (destroyInput) {
      destroyInput.value = '1'
    }
    panel.classList.add('hidden')
    panel.dataset.removed = 'true'

    // Update positions and switch tab
    setTimeout(() => {
      this.updatePositions()
      const remainingPanels = this.getPanels()
      if (remainingPanels.length > 0) {
        const newIndex = Math.min(index, remainingPanels.length - 1)
        this.showTab(newIndex)
      }
    }, 10)
  }

  selectTab(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.showTab(index)
  }

  showTab(index) {
    const panels = this.getPanels()

    // Ensure index is valid
    if (index < 0 || index >= panels.length) {
      index = Math.max(0, panels.length - 1)
    }

    // Hide all panels
    panels.forEach((panel, i) => {
      if (i === index) {
        panel.classList.remove('hidden')
      } else {
        panel.classList.add('hidden')
      }
    })

    this.activeIndexValue = index
    this.updateTabs()
  }

  addVariant() {
    // Trigger the nested-form add action
    const nestedFormController = this.application.getControllerForElementAndIdentifier(
      this.element.closest('[data-controller~="nested-form"]'),
      'nested-form'
    )

    if (nestedFormController) {
      nestedFormController.add({ preventDefault: () => {} })

      // Wait for DOM update then switch to new tab and update variant options
      setTimeout(() => {
        const panels = this.getPanels()
        const newPanel = panels[panels.length - 1]

        // Set position for new variant
        const positionInput = newPanel?.querySelector('input[name*="[position]"]')
        if (positionInput) {
          positionInput.value = panels.length
        }

        this.showTab(panels.length - 1)

        // Dispatch event to notify category-selector to update new variant's options
        this.element.dispatchEvent(new CustomEvent('variant:added', {
          bubbles: true,
          detail: { panel: newPanel }
        }))
      }, 10)
    }
  }

  getPanels() {
    return Array.from(this.panelsContainerTarget.querySelectorAll('.variant-fields'))
      .filter(panel => panel.dataset.removed !== 'true')
  }

  // Called when nested-form removes an item
  variantRemoved() {
    setTimeout(() => {
      this.updatePositions()
      const panels = this.getPanels()
      if (this.activeIndexValue >= panels.length) {
        this.showTab(panels.length - 1)
      } else {
        this.updateTabs()
      }
    }, 10)
  }
}
